/**
 * <AddSchoolAssessmentMedia> — AI / media step for adding a school assessment (organism, L0.1).
 *
 * Intro + `<MediaPicker>` + optional "Analyser" / AI preview / "Remplir à la main".
 * No modal chrome — composed inside the `<AddSchoolAssessmentModal>` template.
 *
 * `analyzeStage` nomme l'étape de l'analyse (« Envoi... », « Analyse en cours... »,
 * « Chargement... ») : la roue reste dans le bouton, qui remplace son libellé par elle, et les mots
 * s'écrivent juste dessous.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { type UISchoolAssessmentPreview } from "../../../ports/primary/ui/types"
import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Divider } from "../../atoms/Divider"
import { Icon } from "../../atoms/icons/Icon"
import { Paragraph } from "../../atoms/typography/Paragraph"
import { Text } from "../../atoms/typography/Text"
import { SchoolAssessmentAIPreview } from "../../molecules/SchoolAssessmentAIPreview"
import { type Voice } from "../../utils/voice"
import { MediaPicker, type MediaPickerProps } from "../MediaPicker"

/**
 * Où en est l'analyse d'une photo : les fichiers partent, l'IA lit, le résultat arrive.
 * À remonter dans `ports/primary/ui/types` au câblage de l'espace élève.
 */
export type SchoolAssessmentAnalyzeStage = "uploading" | "analyzing" | "preparing"

const ANALYZE_LABEL = "Analyser"

const ANALYZE_STAGE_LABEL: Record<SchoolAssessmentAnalyzeStage, string> = {
    uploading: "Envoi...",
    analyzing: "Analyse en cours...",
    preparing: "Chargement...",
}

export type AddSchoolAssessmentMediaProps = {
    mediaProps?: MediaPickerProps
    onAnalyze?: () => void
    analyzeLoading?: boolean
    /**
     * L'étape de l'analyse en cours, écrite sous le bouton. Présente, elle vaut `analyzeLoading`.
     * Absente (défaut) : le bouton tourne sans un mot, comme avant.
     */
    analyzeStage?: SchoolAssessmentAnalyzeStage | null
    /** AI success summary — when set, Analyser / manual entry chrome is hidden. */
    preview?: UISchoolAssessmentPreview | null
    onEditPreview?: () => void
    /** Switch the parent modal to the manual form. */
    onManualPress: () => void
    /** Who reads the intro — the parent space vouvoie its reader. Defaults to the student wording. */
    voice?: Voice
    testID?: string
}

const TEXTS: Record<Voice, { intro: string }> = {
    student: {
        intro: "Prends en photo ton cahier de texte, ton agenda ou une capture de Pronote et on remplit tout pour toi !",
    },
    parent: {
        intro: "Prenez en photo le cahier de texte, l'agenda ou une capture de Pronote et on remplit tout pour vous !",
    },
}

function hasMedia(mediaProps?: MediaPickerProps): boolean {
    const images = mediaProps?.images ?? []
    const pdf = mediaProps?.pdf
    return images.length > 0 || (pdf != null && pdf !== "")
}

export function AddSchoolAssessmentMedia(props: AddSchoolAssessmentMediaProps) {
    const {
        mediaProps,
        onAnalyze,
        analyzeLoading: analyzeLoadingProp = false,
        analyzeStage = null,
        preview = null,
        onEditPreview,
        onManualPress,
        voice = "student",
        testID,
    } = props
    const analyzeLoading = analyzeLoadingProp || analyzeStage != null
    const mediaReady = hasMedia(mediaProps)
    const hasPreview = preview != null
    const stageLabel = analyzeStage != null ? ANALYZE_STAGE_LABEL[analyzeStage] : null
    // Le bouton porte le nom de l'étape pour un lecteur d'écran : sa roue a pris la place du libellé.
    const analyzeButton = (
        <Button label={stageLabel ?? ANALYZE_LABEL} iconName="magic" loading={analyzeLoading} onPress={onAnalyze} />
    )

    return (
        <Stack gap="5" align="stretch" testID={testID}>
            {!mediaReady && !hasPreview && (
                <Stack gap="2" align="center">
                    <Row gap="2" align="center" justify="center">
                        <Icon name="magic" size={18} color="primary" />
                        <Text size="text-lg" weight="bold" color="primary">
                            Analyse automatique
                        </Text>
                    </Row>
                    <Paragraph center color="text-muted" style={styles.copy}>
                        {TEXTS[voice].intro}
                    </Paragraph>
                </Stack>
            )}

            <MediaPicker
                {...mediaProps}
                buttonLabel="Photo ou document"
                multiple
                enableExternalLink={false}
                minHeight={180}
                showAddMoreButton={!analyzeLoading && !hasPreview}
            />

            {mediaReady &&
                !hasPreview &&
                (stageLabel == null ? (
                    analyzeButton
                ) : (
                    <Stack gap="2" align="stretch">
                        {analyzeButton}
                        <Text size="text-sm" color="text-muted" center>
                            {stageLabel}
                        </Text>
                    </Stack>
                ))}

            {hasPreview && onEditPreview != null && (
                <SchoolAssessmentAIPreview preview={preview} onEdit={onEditPreview} />
            )}

            {!analyzeLoading && !hasPreview && (
                <>
                    <Divider label="ou" spacing="0" />
                    <TextButton fullWidth label="Remplir à la main" onPress={onManualPress} />
                </>
            )}
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    copy: {
        alignSelf: "stretch" as const,
    },
}))
